/**
 * Model validation utilities for GLTF/GLB puppet models
 *
 * This module provides utilities to validate that loaded 3D models
 * have the required skeleton structure for animation.
 */

import type { GLTF } from 'three-stdlib';
import type { Object3D } from 'three';
import { SkinnedMesh } from 'three';

// Re-export types for consumers
export type { GLTF as GLTFModel } from 'three-stdlib';
export type { Skeleton, Bone } from 'three';

/**
 * Validates that a GLTF model has a valid skeleton structure for animation
 *
 * A valid model must have at least one SkinnedMesh with a skeleton containing bones.
 *
 * @param model - The loaded GLTF model to validate
 * @returns true if the model has a valid skeleton, false otherwise
 *
 * @example
 * ```typescript
 * const gltf = useGLTF('/models/puppet.glb');
 * if (hasValidSkeleton(gltf)) {
 *   // Model is valid, proceed with animation setup
 * } else {
 *   // Show error to user
 * }
 * ```
 */
export const hasValidSkeleton = (model: GLTF): boolean => {
  if (!model?.scene?.children) {
    return false;
  }

  // Recursively search for SkinnedMesh with skeleton
  const findSkinnedMesh = (object: Object3D): boolean => {
    // Check if this is a SkinnedMesh with a valid skeleton
    if (object instanceof SkinnedMesh && object.skeleton?.bones?.length > 0) {
      return true;
    }

    // Search recursively through children if they exist
    if (object.children && Array.isArray(object.children)) {
      for (const child of object.children) {
        if (findSkinnedMesh(child)) {
          return true;
        }
      }
    }

    return false;
  };

  return findSkinnedMesh(model.scene);
};
